import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook to fetch authenticated files and convert them to blob URLs
 * Used for displaying uploaded videos/documents that require authentication
 */
export const useAuthenticatedFile = (fileUrl) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;

    const fetchFile = async () => {
      // Check if it's an internal API path that needs authentication
      const needsAuth = fileUrl && (
        fileUrl.startsWith('/api/tech-learning/builder/files/') ||
        fileUrl.startsWith('/api/tech-learning/builder/legacy-files/') ||
        fileUrl.includes('/api/tech-learning/builder/legacy-files/') ||
        fileUrl.includes('/api/tech-learning/builder/files/')
      );
      
      if (!fileUrl || !needsAuth) {
        setBlobUrl(fileUrl); // Return original URL for external files
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Extract the API path from full URL if needed
        let apiPath = fileUrl;
        if (fileUrl.includes('/api/')) {
          apiPath = fileUrl.substring(fileUrl.indexOf('/api/'));
        }
        
        const response = await api.get(apiPath, {
          responseType: 'blob',
        });
        
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error('Failed to fetch authenticated file:', err);
        setError(err.message || 'Failed to load file');
        setBlobUrl(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFile();

    // Cleanup blob URL on unmount or URL change
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileUrl]);

  return { blobUrl, loading, error };
};

/**
 * Helper function to check if a URL is an internal storage URL (object storage or legacy)
 */
export const isObjectStorageUrl = (url) => {
  if (!url) return false;
  return url.startsWith('/api/tech-learning/builder/files/') ||
         url.startsWith('/api/tech-learning/builder/legacy-files/') ||
         url.includes('/api/tech-learning/builder/files/') ||
         url.includes('/api/tech-learning/builder/legacy-files/');
};

/**
 * Helper function to check if URL needs authentication
 */
export const needsAuthentication = (url) => {
  if (!url) return false;
  return url.startsWith('/api/') || url.startsWith('/uploads/');
};

/**
 * Component wrapper for authenticated video playback
 */
export const AuthenticatedVideo = ({ src, className, ...props }) => {
  const { blobUrl, loading, error } = useAuthenticatedFile(src);

  if (loading) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <div className="animate-pulse text-muted-foreground">Loading video...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <div className="text-destructive text-sm">Failed to load video</div>
      </div>
    );
  }

  if (!blobUrl) {
    return null;
  }

  return (
    <video src={blobUrl} className={className} {...props}>
      Your browser does not support video playback.
    </video>
  );
};

/**
 * Component wrapper for authenticated document/iframe viewing (PDFs)
 */
export const AuthenticatedDocument = ({ src, className, title, fileType, ...props }) => {
  const { blobUrl, loading, error } = useAuthenticatedFile(src);

  if (loading) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <div className="animate-pulse text-muted-foreground">Loading document...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} bg-muted flex flex-col items-center justify-center gap-2`}>
        <div className="text-destructive text-sm">Failed to load document</div>
        <a 
          href={src} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary text-sm underline"
        >
          Try opening in new tab
        </a>
      </div>
    );
  }

  if (!blobUrl) {
    return null;
  }

  // For PDFs, use iframe with blob URL
  if (fileType === 'pdf') {
    return (
      <iframe
        src={`${blobUrl}#toolbar=0&navpanes=0`}
        className={className}
        title={title}
        {...props}
      />
    );
  }

  // For other documents (Word, etc.), provide download link since browsers can't render them
  return (
    <div className={`${className} bg-muted flex flex-col items-center justify-center gap-4`}>
      <div className="text-muted-foreground text-sm">Document preview not available</div>
      <a 
        href={blobUrl} 
        download={title || 'document'}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
      >
        Download Document
      </a>
    </div>
  );
};

export default useAuthenticatedFile;
